import React, { useCallback, useState, useMemo } from 'react';
import { Search } from '@appkit4/react-components/search';
import Modal from '../../components/reusables/Modal';
import { Button } from '@appkit4/react-components/button';
import IconButton from '../../components/reusables/IconButton';
import MenuButton, { MenuItem } from '../../components/reusables/MenuButton';
import Pagination from '../../components/reusables/Pagination';
import DataTable from '../../components/reusables/DataTable';
import { type ColumnDef } from '@tanstack/react-table';
import DropDown from '../../components/reusables/DropDown';
import '../SharedPage.scss';
import { Tooltip } from '@appkit4/react-components/tooltip';
import { ReactNode } from 'react-markdown/lib/ast-to-react';
import SplitRuleForm, { SplitRuleFormValues } from './SplitRuleForm';
import { ReactComponent as DownloadIcon } from '../../assets/icons/New Brand Icons/download.svg';
import RuleImportModal, { RuleImportProjectOption } from './RuleImportModal';
import {
    useGetRuleList,
    useGetRuleById,
    useDeleteRule,
    useGetRuleBanks,
    useCreateRule,
    useUpdateRule,
    useGetRuleColumns,
    useGetPools,
    useGetProjectsForEntity,
    useGetUserInfo,
} from '../../api/generated/endpoint';
import type { RuleListItemResponse } from '../../api/generated/models';
import { useEngagement } from '../../context/EngagementContext';
import { useTenant } from '../../context/TenantContext';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import useToast from '../../helpers/useToast';

type RuleRow = RuleListItemResponse & {
    actions?: ReactNode;
};

// Mirrors backend RuleTypeId enum. 0 = all types.
const RULE_TYPE_PROJECT_DATA = 6;
const RULE_TYPE_FIXED_ASSET = 7;

const Rules: React.FC = () => {
    const { activeTimeId, selectedEntityId } = useEngagement();
    const showGenericErrorToast = useGenericErrorToast();
    const showToast = useToast();
    const { selectedTenant } = useTenant();
    const { data: userInfo } = useGetUserInfo();
    // FASORT did not offer rule imports to reviewers; the API rejects them as well.
    const isReviewer =
        userInfo?.tenants
            ?.find((tenant) => tenant.tenantId === selectedTenant?.tenantId)
            ?.roleKey?.toUpperCase() === 'REVIEWER';
    const [rulesSearch, setRulesSearch] = useState('');

    const [isDeleteRuleModalVisible, setIsDeleteRuleModalVisible] =
        useState<boolean>(false);
    const [isSplitRuleModalVisible, setIsSplitRuleModalVisible] =
        useState<boolean>(false);
    const [isEditSplitRuleModalVisible, setIsEditSplitRuleModalVisible] =
        useState<boolean>(false);
    const [editSplitRuleInitialValues, setEditSplitRuleInitialValues] =
        useState<SplitRuleFormValues | undefined>(undefined);

    const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
    const [selectedRules, setSelectedRules] = useState<any[]>([]);
    const [isBulkDeleteModalVisible, setIsBulkDeleteModalVisible] =
        useState<boolean>(false);
    const [isUploadModalVisible, setIsUploadModalVisible] =
        useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [splitRuleFormKey, setSplitRuleFormKey] = useState(0);

    // Applied rule banks. Drives which rule types the list shows — 0 means all types.
    const [selectedRuleBanks, setSelectedRuleBanks] = useState<string[]>([]);

    // Rule type the Add/Edit form is working with, independent of the applied banks.
    const [formRuleTypeId, setFormRuleTypeId] = useState<number>(
        RULE_TYPE_FIXED_ASSET
    );

    // API queries — ruleBanksData must be declared before the useEffect that reads it
    const { data: ruleBanksData } = useGetRuleBanks(
        { entityId: selectedEntityId!, timeId: activeTimeId! },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    // Initialise selected banks from API response
    const initialisedBanksKey = React.useRef<string>();

    React.useEffect(() => {
        if (!ruleBanksData) return;

        // Refetches return a new array each time, so re-seeding would undo the user's toggle.
        const key = `${selectedEntityId}-${activeTimeId}`;
        if (initialisedBanksKey.current === key) return;
        initialisedBanksKey.current = key;

        setSelectedRuleBanks(
            ruleBanksData
                .filter((b) => b.isApplied)
                .map((b) => String(b.ruleTypeId))
        );
    }, [ruleBanksData, selectedEntityId, activeTimeId]);

    // Ticked banks scope Standard rules only; manual and generated rules always show.
    const bankRuleTypeIds = useMemo(
        () => selectedRuleBanks.map((id) => parseInt(id, 10)),
        [selectedRuleBanks]
    );

    // API queries
    const { data: rulesData, refetch: refetchRules } = useGetRuleList(
        {
            entityId: selectedEntityId,
            timeId: activeTimeId,
            bankRuleTypeIds,
        },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    // Whether the entity has any importable data. Independent of what the form is editing.
    const { data: fixedAssetColumns, isLoading: isLoadingFixedAssetColumns } =
        useGetRuleColumns(
            {
                entityId: selectedEntityId!,
                timeId: activeTimeId!,
                ruleTypeId: RULE_TYPE_FIXED_ASSET,
            },
            { query: { enabled: !!selectedEntityId && !!activeTimeId } }
        );

    const { data: projectDataColumns, isLoading: isLoadingProjectDataColumns } =
        useGetRuleColumns(
            {
                entityId: selectedEntityId!,
                timeId: activeTimeId!,
                ruleTypeId: RULE_TYPE_PROJECT_DATA,
            },
            { query: { enabled: !!selectedEntityId && !!activeTimeId } }
        );

    const { data: poolsData } = useGetPools(
        { entityId: selectedEntityId!, timeId: activeTimeId! },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    // Project scope options for project data rules.
    const { data: projectsData } = useGetProjectsForEntity(
        { entityId: selectedEntityId!, timeId: activeTimeId! },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    const projectOptions = useMemo(() => {
        if (!projectsData) return [];
        return projectsData.map((p) => ({
            value: String(p.projectID),
            label: p.projectName ?? p.projectRef ?? `Project ${p.projectID}`,
        }));
    }, [projectsData]);

    // A project's rules resolve against its imported columns, so projects without data cannot be chosen.
    const ruleImportProjects: RuleImportProjectOption[] = useMemo(
        () =>
            (projectsData ?? []).map((p) => ({
                projectId: p.projectID ?? 0,
                label:
                    p.projectName ?? p.projectRef ?? `Project ${p.projectID}`,
                hasData:
                    (p.availableVersions?.length ?? 0) > 0 ||
                    p.activeVersion != null,
            })),
        [projectsData]
    );

    // Each data type unlocks its own rules once imported.
    const hasFixedAssetData = (fixedAssetColumns?.length ?? 0) > 0;
    const hasProjectData = (projectDataColumns?.length ?? 0) > 0;
    const hasData = hasFixedAssetData || hasProjectData;

    const isLoadingColumns =
        isLoadingFixedAssetColumns || isLoadingProjectDataColumns;

    const ruleTypeOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];

        if (hasFixedAssetData) {
            options.push({
                value: String(RULE_TYPE_FIXED_ASSET),
                label: 'Fixed Asset Data',
            });
        }

        if (hasProjectData) {
            options.push({
                value: String(RULE_TYPE_PROJECT_DATA),
                label: 'Project Data',
            });
        }

        return options;
    }, [hasFixedAssetData, hasProjectData]);

    // Target fields follow the rule type the form is editing.
    const columnOptions = useMemo(() => {
        const columnsData =
            formRuleTypeId === RULE_TYPE_PROJECT_DATA
                ? projectDataColumns
                : fixedAssetColumns;

        if (!columnsData) return [];
        return columnsData.map((c) => ({
            value: String(c.columnId),
            label: c.displayName ?? c.columnKey ?? `Column ${c.columnId}`,
        }));
    }, [formRuleTypeId, fixedAssetColumns, projectDataColumns]);

    const poolOptions = useMemo(() => {
        if (!poolsData) return [];
        return poolsData.map((p) => ({
            value: String(p.poolID),
            label: p.poolShortName ?? `Pool ${p.poolID}`,
        }));
    }, [poolsData]);

    // Mutations
    const { mutateAsync: deleteRuleMutation } = useDeleteRule();
    const { mutateAsync: createRuleMutation } = useCreateRule();
    const { mutateAsync: updateRuleMutation } = useUpdateRule();

    const {
        data: editRuleData,
        isError: isEditRuleError,
        isLoading: isLoadingEditRule,
    } = useGetRuleById(selectedRuleId!, {
        query: {
            enabled: isEditSplitRuleModalVisible && selectedRuleId != null,
        },
    });

    const handleOpenDeleteRuleModal = useCallback((ruleID: number) => {
        setSelectedRuleId(ruleID);
        setIsDeleteRuleModalVisible(true);
    }, []);

    const handleCloseDeleteRuleModal = () => {
        setIsDeleteRuleModalVisible(false);
    };

    const handleOpenBulkDeleteModal = () => {
        setIsBulkDeleteModalVisible(true);
    };

    const handleCloseBulkDeleteModal = () => {
        setIsBulkDeleteModalVisible(false);
    };

    const handleConfirmBulkDelete = async () => {
        try {
            for (const rule of selectedRules) {
                await deleteRuleMutation({ ruleId: rule.ruleId });
            }
            setSelectedRules([]);
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            // Deletes are not atomic, so always re-read the server state.
            setIsBulkDeleteModalVisible(false);
            refetchRules();
        }
    };

    const handleOpenEditRuleModal = useCallback((rule: RuleRow) => {
        setSelectedRuleId(rule.ruleId ?? null);
        setEditSplitRuleInitialValues(undefined);
        setIsEditSplitRuleModalVisible(true);
    }, []);

    React.useEffect(() => {
        if (!editRuleData || !isEditSplitRuleModalVisible) return;

        if (editRuleData.ruleTypeId) {
            setFormRuleTypeId(editRuleData.ruleTypeId);
        }

        setEditSplitRuleInitialValues({
            splitRuleName: editRuleData.shortName ?? '',
            description: editRuleData.longName ?? '',
            priority: String(editRuleData.priority ?? ''),
            ruleType: String(editRuleData.ruleTypeId ?? ''),
            projectId: String(editRuleData.projectId ?? 0),
            keywordRows: (editRuleData.conditions ?? []).map((c, i) => ({
                id: i + 1,
                keywords: c.keywords ?? '',
                targetField: String(c.columnId ?? ''),
            })),
            poolAssignments: (editRuleData.splits ?? []).map((s, i) => ({
                id: i + 1,
                poolCode: String(s.poolId ?? ''),
                percentage: String(s.splitPercent ?? ''),
            })),
        });
    }, [editRuleData, isEditSplitRuleModalVisible]);
    const handleCreateSplitRule = async (values: SplitRuleFormValues) => {
        if (!selectedEntityId || !activeTimeId) return;
        try {
            await createRuleMutation({
                data: {
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    shortName: values.splitRuleName,
                    longName: values.description,
                    priority: parseInt(values.priority, 10),
                    ruleTypeId: parseInt(values.ruleType, 10),
                    projectId: parseInt(values.projectId, 10) || 0,
                    conditions: values.keywordRows
                        .filter((r) => r.keywords.trim())
                        .map((r) => ({
                            columnId: parseInt(r.targetField, 10),
                            keywords: r.keywords,
                        })),
                    splits: values.poolAssignments
                        .filter((p) => p.percentage)
                        .map((p) => ({
                            poolId: parseInt(p.poolCode, 10),
                            splitPercent: parseFloat(p.percentage),
                        })),
                },
            });
            setIsSplitRuleModalVisible(false);
            refetchRules();
        } catch (error: any) {
            showGenericErrorToast(error);
        }
    };

    const handleEditSplitRuleSubmit = async (values: SplitRuleFormValues) => {
        if (selectedRuleId == null) return;
        try {
            await updateRuleMutation({
                ruleId: selectedRuleId,
                data: {
                    shortName: values.splitRuleName,
                    longName: values.description,
                    priority: parseInt(values.priority, 10),
                    ruleTypeId: parseInt(values.ruleType, 10),
                    conditions: values.keywordRows
                        .filter((r) => r.keywords.trim())
                        .map((r) => ({
                            columnId: parseInt(r.targetField, 10),
                            keywords: r.keywords,
                        })),
                    splits: values.poolAssignments
                        .filter((p) => p.percentage)
                        .map((p) => ({
                            poolId: parseInt(p.poolCode, 10),
                            splitPercent: parseFloat(p.percentage),
                        })),
                },
            });
            setIsEditSplitRuleModalVisible(false);
            setEditSplitRuleInitialValues(undefined);
            refetchRules();
        } catch (error: any) {
            showGenericErrorToast(error);
        }
    };

    const handleCloseSplitRuleModal = () => {
        setIsSplitRuleModalVisible(false);
    };

    const handleCloseEditSplitRuleModal = () => {
        setIsEditSplitRuleModalVisible(false);
        setEditSplitRuleInitialValues(undefined);
    };

    const handleDeleteRuleButton = async () => {
        if (selectedRuleId === null) return;
        try {
            await deleteRuleMutation({ ruleId: selectedRuleId });
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            handleCloseDeleteRuleModal();
            refetchRules();
        }
    };

    const addRuleMenuItems: MenuItem[] = [
        ...(isReviewer
            ? []
            : [
                  {
                      label: 'Import Rule',
                      value: 'import',
                      testId: 'rules-import-rule-item',
                  },
              ]),
        { label: 'Split Rule', value: 'split' },
    ];

    const handleAddRuleSelect = (value: string) => {
        if (!hasData) return;
        if (value === 'split') {
            // Default a new rule to the only ticked bank, else whichever data type is imported.
            setFormRuleTypeId(
                bankRuleTypeIds.length === 1
                    ? bankRuleTypeIds[0]
                    : hasFixedAssetData
                      ? RULE_TYPE_FIXED_ASSET
                      : RULE_TYPE_PROJECT_DATA
            );
            setSplitRuleFormKey((k) => k + 1);
            setIsSplitRuleModalVisible(true);
        }
        if (value === 'import') {
            setIsUploadModalVisible(true);
        }
    };

    const ruleBankOptions = useMemo(() => {
        if (!ruleBanksData) return [];
        return ruleBanksData.map((b) => ({
            value: String(b.ruleTypeId),
            label: b.projectType ?? `Type ${b.ruleTypeId}`,
        }));
    }, [ruleBanksData]);

    // Purely a view filter — bank rules are copied on import and are never removed from here.
    const handleRuleBankSelect = (value: string | string[]) => {
        setSelectedRuleBanks(Array.isArray(value) ? value : [value]);
    };

    const filteredRulesData = useMemo(() => {
        if (!rulesData) return [];
        if (!rulesSearch) return rulesData;
        const term = rulesSearch.toLowerCase();
        return rulesData.filter((r) =>
            [
                r.shortName,
                r.longName,
                r.keywords,
                r.columns,
                r.pools,
                r.type,
                r.source,
            ]
                .filter(Boolean)
                .some((v) => v!.toLowerCase().includes(term))
        );
    }, [rulesData, rulesSearch]);

    const paginatedRulesData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredRulesData.slice(start, start + pageSize);
    }, [filteredRulesData, currentPage, pageSize]);

    // Deletes, bank changes and refetches can shrink the result set below the current page.
    React.useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(filteredRulesData.length / pageSize)
        );

        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [filteredRulesData.length, pageSize, currentPage]);

    // A change of scope is a different result set, so start from the first page.
    React.useEffect(() => {
        setCurrentPage(1);
    }, [bankRuleTypeIds, selectedEntityId, activeTimeId]);

    const handleRowSelectionChange = useCallback((rows: RuleRow[]) => {
        setSelectedRules(rows);
    }, []);

    const tableDataWithActions: RuleRow[] = useMemo(
        () =>
            paginatedRulesData.map((rule) => ({
                ...rule,
                actions: (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                            onClick={() =>
                                handleOpenDeleteRuleModal(rule.ruleId!)
                            }
                            icon="Appkit4-icon icon-delete-outline"
                            title="Delete"
                            data-testid={`rule-delete-btn-${rule.ruleId}`}
                        />
                        <IconButton
                            onClick={() => handleOpenEditRuleModal(rule)}
                            icon="Appkit4-icon icon-edit-outline"
                            title="Edit"
                            data-testid={`rule-edit-btn-${rule.ruleId}`}
                        />
                    </div>
                ),
            })),
        [paginatedRulesData]
    );

    // Same columns, in the same order, as FASORT's rules table.
    const rulesColumns: ColumnDef<RuleRow, any>[] = useMemo(
        () => [
            { accessorKey: 'shortName', header: 'Rule Name' },
            { accessorKey: 'longName', header: 'Description' },
            { accessorKey: 'keywords', header: 'Keywords' },
            { accessorKey: 'columns', header: 'Columns' },
            { accessorKey: 'pools', header: 'Pool' },
            { accessorKey: 'priority', header: 'Priority' },
            { accessorKey: 'type', header: 'Type' },
            { accessorKey: 'source', header: 'Source' },
            {
                id: 'actions',
                header: '',
                cell: ({ row }) => row.original.actions,
            },
        ],
        []
    );

    const renderRulesTabContent = () => (
        <div>
            <div className="rules-search-row-firstpage">
                <div className="rules-search-left">
                    <Search
                        className="px-0"
                        data-testid="rules-search"
                        value={rulesSearch}
                        onChange={(value) => {
                            setRulesSearch(value);
                            setCurrentPage(1);
                        }}
                        disabled={!hasData}
                    />
                </div>
                <div className="rules-search-right">
                    <DropDown
                        data={ruleBankOptions}
                        placeholder="Toggle Rule Bank"
                        value={selectedRuleBanks}
                        onSelect={handleRuleBankSelect}
                        multiple
                    />
                </div>
            </div>

            <div className="rules-table-wrapper" data-testid="rules-table">
                <DataTable
                    data={hasData ? tableDataWithActions : []}
                    columns={rulesColumns}
                    columnFiltering
                    rowSelection={hasData}
                    getRowId={(row) => String(row.ruleId)}
                    onRowSelectionChange={
                        hasData ? handleRowSelectionChange : undefined
                    }
                />
                {!hasData && (
                    <p
                        style={{
                            textAlign: 'center',
                            padding: '24px',
                            color: '#666',
                        }}
                    >
                        {isLoadingColumns
                            ? 'Loading rules…'
                            : 'No data has been imported for this entity and time period. Please import data before creating rules.'}
                    </p>
                )}
            </div>

            {hasData && (
                <div className="rules-pagination">
                    <Pagination
                        totalItems={filteredRulesData.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className="rules-page" data-testid="rules-page">
            <h3 className="rules-header" data-testid="rules-heading">
                Rules
            </h3>

            <div className="rules-top-row">
                <div className="rules-top-right">
                    <Button
                        kind="text"
                        disabled={!hasData}
                        onClick={() => {
                            // TODO: download template
                        }}
                    >
                        <span
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#000',
                            }}
                        >
                            <DownloadIcon width={16} height={16} />
                            Download Template
                        </span>
                    </Button>

                    <Button
                        kind="text"
                        disabled={!hasData || selectedRules.length === 0}
                        onClick={handleOpenBulkDeleteModal}
                        data-testid="rules-delete-selected-btn"
                    >
                        <span
                            style={{
                                color:
                                    !hasData || selectedRules.length === 0
                                        ? '#999'
                                        : '#000',
                            }}
                        >
                            Delete Selected
                        </span>
                    </Button>

                    {hasData ? (
                        <MenuButton
                            label="Add Rule"
                            items={addRuleMenuItems}
                            onSelect={handleAddRuleSelect}
                            testId="rules-add-rule-menu"
                        />
                    ) : (
                        <Tooltip
                            content="Data import required before adding rules"
                            position="top"
                        >
                            <Button kind="primary" disabled>
                                Add Rule
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>

            {renderRulesTabContent()}

            <Modal
                visible={isDeleteRuleModalVisible}
                onClose={handleCloseDeleteRuleModal}
                title="Delete Rule?"
            >
                <div className="engagementModal">
                    <p>
                        Are you sure you want to delete this rule? This action
                        is permanent.
                    </p>
                    <div className="deleteButtons">
                        <Button
                            onClick={handleCloseDeleteRuleModal}
                            kind="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteRuleButton}
                            kind="primary"
                            data-testid="rules-confirm-delete-btn"
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                visible={isSplitRuleModalVisible}
                onClose={handleCloseSplitRuleModal}
                title="New Split Rule"
            >
                <SplitRuleForm
                    key={splitRuleFormKey}
                    onSubmit={handleCreateSplitRule}
                    onRuleTypeChange={(value) =>
                        setFormRuleTypeId(parseInt(value, 10))
                    }
                    ruleTypeOptions={ruleTypeOptions}
                    targetFieldOptions={columnOptions}
                    poolCodeOptions={poolOptions}
                    projectOptions={projectOptions}
                />
            </Modal>
            <Modal
                visible={isEditSplitRuleModalVisible}
                onClose={handleCloseEditSplitRuleModal}
                title="Edit Split Rule"
            >
                {editSplitRuleInitialValues ? (
                    <SplitRuleForm
                        initialValues={editSplitRuleInitialValues}
                        onSubmit={handleEditSplitRuleSubmit}
                        onRuleTypeChange={(value) =>
                            setFormRuleTypeId(parseInt(value, 10))
                        }
                        ruleTypeOptions={ruleTypeOptions}
                        targetFieldOptions={columnOptions}
                        poolCodeOptions={poolOptions}
                        projectOptions={projectOptions}
                    />
                ) : isEditRuleError ? (
                    <p>
                        This rule could not be loaded. Please close and retry.
                    </p>
                ) : isLoadingEditRule ? (
                    <p>Loading rule...</p>
                ) : (
                    <p>This rule is no longer available.</p>
                )}
            </Modal>
            <Modal
                visible={isBulkDeleteModalVisible}
                onClose={handleCloseBulkDeleteModal}
                title="Delete Selected Rules?"
            >
                <div className="engagementModal">
                    <p>
                        Are you sure you want to delete {selectedRules.length}{' '}
                        selected rule{selectedRules.length !== 1 ? 's' : ''}?
                        This action is permanent.
                    </p>
                    <div className="deleteButtons">
                        <Button
                            onClick={handleCloseBulkDeleteModal}
                            kind="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmBulkDelete}
                            kind="primary"
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Mounted only while open, like the data import modals: Appkit's focus trap breaks on modals kept mounted between opens. */}
            {isUploadModalVisible && (
                <RuleImportModal
                    entityId={selectedEntityId ?? 0}
                    timeId={activeTimeId ?? 0}
                    hasFixedAssetData={hasFixedAssetData}
                    hasProjectData={hasProjectData}
                    projects={ruleImportProjects}
                    onClose={() => setIsUploadModalVisible(false)}
                    onImported={(rulesAdded) => {
                        setIsUploadModalVisible(false);
                        showToast(
                            'Success',
                            `Import succeeded. ${rulesAdded} rule${
                                rulesAdded === 1 ? ' was' : 's were'
                            } added.`,
                            'success'
                        );
                        refetchRules();
                    }}
                />
            )}
        </div>
    );
};

export default Rules;
